import React, { useState } from "react"
import {
  Box, Divider, HStack, Stack, Text,
} from "@chakra-ui/react"
import Button from "components/Buttons/Button"
import Spinner from "components/elements/Spinner"
import NewTextMenu from "pages/EventShow/Texts/NewTextMenu"
import { useTextDeliveryCounts } from "queries/texts"
import { TextMessageDeliveryCounts } from "sharedTypes"
import DashboardCard, { EmptyState } from "../components/DashboardCard"
import SegmentedControl from "../components/SegmentedControl"
import { BigStat, Legend, ProgressRows } from "../components/charts"

type Channel = "all" | "sms" | "whatsapp"
type Counts = TextMessageDeliveryCounts

const DELIVERY = [
  ["Delivered", "delivered", "textStatus.delivered"],
  ["Sent", "sent", "textStatus.sent"],
  ["Undelivered", "undelivered", "textStatus.undelivered"],
  ["Failed", "failed", "textStatus.failed"],
  ["Delivery Unknown", "deliveryUnknown", "textStatus.deliveryUnknown"],
] as const

const OPT_IN = [
  ["Awaiting Opt-In", "awaitingOptIn", "textStatus.awaiting_opt_in"],
  ["Opt-In Declined", "optInDeclined", "textStatus.opt_in_declined"],
  ["Opt-In Expired", "optInExpired", "textStatus.opt_in_expired"],
] as const

const combine = (a: Counts, b: Counts) => Object.fromEntries(
  Object.keys(a).map((key) => [key, (a[key] ?? 0) + (b[key] ?? 0)]),
) as Counts

const sentTotal = (counts: Counts) => DELIVERY.reduce((sum, [, key]) => sum + (counts[key] ?? 0), 0)

const optInTotal = (counts: Counts) => OPT_IN.reduce((sum, [, key]) => sum + (counts[key] ?? 0), 0)

type Props = {
  eventId: string
}

const TextsCard = ({ eventId }: Props) => {
  const [channel, setChannel] = useState<Channel>("all")
  const { data, isLoading, isError } = useTextDeliveryCounts(eventId)

  if (isError) {
    return (
      <DashboardCard title="Text Messages">
        <Text color="gray.600">Delivery counts couldn&apos;t be loaded.</Text>
      </DashboardCard>
    )
  }

  if (isLoading || !data) {
    return (
      <DashboardCard title="Text Messages">
        <Spinner />
      </DashboardCard>
    )
  }

  const { sms, whatsapp } = data
  const all = combine(sms, whatsapp)
  const totals = { all, sms, whatsapp }[channel]
  const showOptIn = channel === "whatsapp" || (channel === "all" && optInTotal(whatsapp) > 0)
  const total = sentTotal(totals)

  if (!sentTotal(all) && !all.queued && !optInTotal(all)) {
    return (
      <DashboardCard title="Text Messages">
        <EmptyState
          title="No text messages sent"
          body="Send an SMS or WhatsApp message to see delivery."
          action={(
            <NewTextMenu
              button={<Button as="span" size="sm" variant="outline">New text message</Button>}
            />
          )}
        />
      </DashboardCard>
    )
  }

  return (
    <DashboardCard
      title="Text Messages"
      action={(
        <SegmentedControl
          value={channel}
          onChange={setChannel}
          options={[
            { value: "all", label: "All" },
            { value: "sms", label: "SMS" },
            { value: "whatsapp", label: "WhatsApp" },
          ]}
        />
      )}
    >
      <BigStat label="Total messages sent" value={total} />
      {channel === "all" && (
        <HStack justify="flex-end" spacing={3} mt={1} fontSize="xs" color="gray.600">
          <Text>SMS {sentTotal(sms).toLocaleString()}</Text>
          <Text>WhatsApp {sentTotal(whatsapp).toLocaleString()}</Text>
        </HStack>
      )}
      <Stack spacing={0} mt={4}>
        <ProgressRows
          total={total}
          rows={DELIVERY.map(([label, key, color]) => ({ label, value: totals[key], color }))}
        />
      </Stack>
      {totals.queued > 0 && (
        <Text fontSize="xs" color="gray.600" mt={2}>{totals.queued.toLocaleString()} still queued</Text>
      )}
      {showOptIn && (
        <Box>
          <Divider my={4} />
          <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>WhatsApp opt-in</Text>
          <Legend
            items={OPT_IN.map(([label, key, color]) => ({ label, value: whatsapp[key], color }))}
            total={optInTotal(whatsapp)}
          />
        </Box>
      )}
    </DashboardCard>
  )
}

export default TextsCard
